"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { API_ENDPOINTS } from "@/constants/config";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft, Plus, Pencil, Trash2, X, Eye, EyeOff,
  Star, StarOff, Search, FileText, Loader2, Upload,
  ImageIcon, RefreshCw, Settings, ChevronDown, Type,
} from "lucide-react";
import toast from "react-hot-toast";

// ─────────────────────────────────────────────────────────────────────────────
const CATEGORIES = ["Technology", "Safety", "Finance", "Education", "Communication", "General"];

const BLOCK_TYPES = [
  { value: "paragraph",  label: "Normal",      badge: "Normal" },
  { value: "heading",    label: "Heading",     badge: "H2"     },
  { value: "subheading", label: "Sub-heading", badge: "H3"     },
  { value: "quote",      label: "Quote",       badge: "Quote"  },
  { value: "list",       label: "Bullet List", badge: "List"   },
];

const toSlug = (text) =>
  text.toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

const formatDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const emptyForm = () => ({
  title: "", slug: "", _slugManual: false,
  excerpt: "", category: "", coverImage: "",
  readTime: "5 min read", author: "Clynox Team", authorDesignation: "",
  tags: [], isFeatured: false, isPublished: false, content: [],
});

// ── Auto-resize textarea ──────────────────────────────────────────────────────
function AutoTextarea({ value, onChange, onKeyDown, onFocus, placeholder, className }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  return (
    <textarea
      ref={ref}
      value={value}
      onChange={onChange}
      onKeyDown={onKeyDown}
      onFocus={onFocus}
      placeholder={placeholder}
      rows={1}
      className={`block w-full resize-none overflow-hidden bg-transparent border-none outline-none focus:outline-none leading-relaxed placeholder:text-gray-300 ${className}`}
    />
  );
}

// ── Tag Input ─────────────────────────────────────────────────────────────────
function TagInput({ tags, onChange }) {
  const [input, setInput] = useState("");
  const add = (raw) => {
    const t = raw.trim().replace(/,+$/, "");
    if (t && !tags.includes(t)) onChange([...tags, t]);
  };
  return (
    <div className="flex flex-wrap gap-1.5 p-2 border rounded-lg bg-white min-h-[40px] focus-within:ring-2 focus-within:ring-blue-300">
      {tags.map((t) => (
        <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full font-medium">
          {t}<button type="button" onClick={() => onChange(tags.filter((x) => x !== t))}><X className="w-3 h-3" /></button>
        </span>
      ))}
      <input
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(input); setInput(""); }
          else if (e.key === "Backspace" && !input && tags.length) onChange(tags.slice(0, -1));
        }}
        onBlur={() => { if (input.trim()) { add(input); setInput(""); } }}
        placeholder={tags.length ? "" : "Add tag, press Enter…"}
        className="flex-1 min-w-[120px] outline-none text-sm bg-transparent placeholder:text-gray-400"
      />
    </div>
  );
}

// ── Cover Image Uploader ──────────────────────────────────────────────────────
function CoverUploader({ value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const ref = useRef(null);

  const handleFile = async (file) => {
    if (!file || !file.type.startsWith("image/")) { toast.error("Select an image"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const res = await fetch(API_ENDPOINTS.superadminBlogImageUpload, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        credentials: "include",
        body: fd,
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || "Upload failed");
      onChange(json.data.url);
      toast.success("Cover uploaded!");
    } catch (e) { toast.error(e?.message || "Upload failed"); }
    finally { setUploading(false); }
  };

  if (value) {
    return (
      <div className="relative group w-full h-56 bg-gray-100">
        <img src={value} alt="cover" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
          <button onClick={() => ref.current?.click()} className="flex items-center gap-1.5 px-3 py-1.5 bg-white/90 text-gray-800 text-sm rounded-lg font-medium hover:bg-white">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Change
          </button>
          <button onClick={() => onChange("")} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/90 text-white text-sm rounded-lg font-medium hover:bg-red-600">
            <X className="w-4 h-4" /> Remove
          </button>
        </div>
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files[0])} />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => ref.current?.click()}
      className="w-full h-36 flex flex-col items-center justify-center gap-2 text-blue-500 hover:bg-blue-50 transition-colors"
    >
      {uploading
        ? <Loader2 className="w-6 h-6 animate-spin" />
        : <ImageIcon className="w-6 h-6" />}
      <span className="text-sm font-medium">{uploading ? "Uploading…" : "Add Cover photo"}</span>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files[0])} />
    </button>
  );
}

// ── Settings Drawer ───────────────────────────────────────────────────────────
function SettingsDrawer({ form, set, open, onClose }) {
  return (
    <>
      {/* Backdrop */}
      {open && <div className="fixed inset-0 z-20" onClick={onClose} />}
      {/* Panel */}
      <div className={`fixed top-0 right-0 h-full w-80 bg-white shadow-2xl z-30 flex flex-col transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"}`}>
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <span className="font-semibold text-gray-800">Post Settings</span>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100"><X className="w-4 h-4 text-gray-500" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Slug</Label>
            <div className="flex items-center gap-1 text-sm border rounded-lg px-3 py-2 bg-gray-50">
              <span className="text-gray-400 text-xs shrink-0">/blog/</span>
              <input
                value={form.slug}
                onChange={(e) => set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
                onFocus={() => set("_slugManual", true)}
                className="flex-1 text-xs font-mono text-gray-700 bg-transparent outline-none min-w-0"
              />
              {form._slugManual && (
                <button onClick={() => { set("slug", toSlug(form.title)); set("_slugManual", false); }} className="text-blue-500 hover:text-blue-700 shrink-0">
                  <RefreshCw className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Excerpt *</Label>
            <textarea
              value={form.excerpt}
              onChange={(e) => set("excerpt", e.target.value)}
              placeholder="Short description shown on blog listing…"
              rows={3}
              className="w-full border rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Category *</Label>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger className="bg-white"><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Tags</Label>
            <TagInput tags={form.tags} onChange={(v) => set("tags", v)} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Read Time</Label>
              <Input value={form.readTime} onChange={(e) => set("readTime", e.target.value)} className="text-sm" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Author</Label>
              <Input value={form.author} onChange={(e) => set("author", e.target.value)} className="text-sm" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Designation</Label>
            <Input
              value={form.authorDesignation}
              onChange={(e) => set("authorDesignation", e.target.value)}
              placeholder="e.g. CEO, Clynox"
              className="text-sm"
            />
          </div>

          <div className="space-y-3 pt-2 border-t">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">Featured</p>
                <p className="text-xs text-gray-400">Show at top of blog</p>
              </div>
              <Switch checked={form.isFeatured} onCheckedChange={(v) => set("isFeatured", v)} />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">Published</p>
                <p className="text-xs text-gray-400">Visible to public</p>
              </div>
              <Switch checked={form.isPublished} onCheckedChange={(v) => set("isPublished", v)} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Blog Editor ───────────────────────────────────────────────────────────────
function BlogEditor({ initial, onSave, onBack, saving }) {
  const [form, setForm] = useState(() => initial ? { ...initial, _slugManual: !!initial.slug } : emptyForm());
  const [focusedIdx, setFocusedIdx] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [typeMenuOpen, setTypeMenuOpen] = useState(false);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));
  const setMany = (patch) => setForm((f) => ({ ...f, ...patch }));

  const handleTitleChange = (title) => {
    setForm((f) => ({ ...f, title, slug: f._slugManual ? f.slug : toSlug(title) }));
  };

  // ── Block operations ──
  const addBlock = (type, afterIdx = null) => {
    setForm((f) => {
      const next = [...f.content];
      const idx = afterIdx !== null ? afterIdx + 1 : next.length;
      next.splice(idx, 0, { type, text: "", items: [] });
      return { ...f, content: next };
    });
    const newIdx = afterIdx !== null ? afterIdx + 1 : form.content.length;
    setTimeout(() => {
      const areas = document.querySelectorAll(".editor-block-textarea");
      if (areas[newIdx]) areas[newIdx].focus();
    }, 20);
  };

  const updateBlock = (idx, patch) => setForm((f) => {
    const next = [...f.content];
    next[idx] = { ...next[idx], ...patch };
    return { ...f, content: next };
  });

  const deleteBlock = (idx) => {
    setForm((f) => ({ ...f, content: f.content.filter((_, i) => i !== idx) }));
    setFocusedIdx(Math.max(0, idx - 1));
    setTimeout(() => {
      const areas = document.querySelectorAll(".editor-block-textarea");
      const target = areas[Math.max(0, idx - 1)];
      if (target) target.focus();
    }, 20);
  };

  const changeType = (type) => {
    if (focusedIdx !== null) updateBlock(focusedIdx, { type });
    setTypeMenuOpen(false);
  };

  const handleBlockKeyDown = (e, idx) => {
    const block = form.content[idx];
    const isSingleLine = block.type === "heading" || block.type === "subheading";

    if (e.key === "Enter" && isSingleLine) {
      e.preventDefault();
      addBlock("paragraph", idx);
    } else if (e.key === "Enter" && block.type === "quote") {
      e.preventDefault();
      addBlock("paragraph", idx);
    } else if (e.key === "Backspace" && !block.text) {
      e.preventDefault();
      deleteBlock(idx);
    }
  };

  const handleSubmit = (publish) => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (!form.excerpt.trim()) { toast.error("Excerpt is required — open Settings"); return; }
    if (!form.category) { toast.error("Category is required — open Settings"); return; }
    const { _slugManual, ...rest } = form;
    onSave({
      ...rest,
      isPublished: publish !== undefined ? publish : form.isPublished,
      content: form.content.map((b) =>
        b.type === "list"
          ? { type: b.type, items: (b.text || "").split("\n").map((s) => s.replace(/^[-•*]\s*/, "").trim()).filter(Boolean), text: "" }
          : { type: b.type, text: b.text || "", items: [] }
      ),
    });
  };

  const currentBlockType = focusedIdx !== null && form.content[focusedIdx]
    ? BLOCK_TYPES.find((t) => t.value === form.content[focusedIdx].type)
    : null;

  const BLOCK_STYLE = {
    heading:    "text-2xl font-bold text-gray-900 placeholder:font-bold",
    subheading: "text-lg font-semibold text-gray-800",
    paragraph:  "text-base text-gray-700",
    quote:      "text-base italic text-gray-600 border-l-4 border-blue-400 pl-4",
    list:       "text-base text-gray-700",
  };

  return (
    <div className="min-h-screen bg-[#f5f5f5]" onClick={() => setTypeMenuOpen(false)}>
      {/* ── Top action bar ── */}
      <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-2.5 bg-white border-b shadow-sm">
        <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          <span className="hidden sm:inline">Back to list</span>
        </button>

        <div className="flex items-center gap-2">
          <span className={`text-xs px-2 py-1 rounded-full font-medium ${form.isPublished ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
            {form.isPublished ? "● Live" : "○ Draft"}
          </span>
          <button
            onClick={() => setSettingsOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Settings className="w-4 h-4" />
            <span className="hidden sm:inline">Settings</span>
          </button>
          <Button variant="outline" size="sm" onClick={() => handleSubmit(false)} disabled={saving}>
            {saving && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
            Save Draft
          </Button>
          <Button size="sm" onClick={() => handleSubmit(true)} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
            {saving && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
            {form.isPublished ? "Update" : "Publish"}
          </Button>
        </div>
      </div>

      {/* ── Document card ── */}
      <div className="max-w-3xl mx-auto my-8 bg-white rounded-xl shadow-sm overflow-hidden">

        {/* Cover photo */}
        <CoverUploader value={form.coverImage} onChange={(v) => set("coverImage", v)} />

        {/* ── Formatting toolbar ── */}
        <div className="sticky top-[49px] z-10 flex items-center gap-1 px-4 py-2 bg-gray-900 text-white text-sm" onClick={(e) => e.stopPropagation()}>
          {/* Block type selector */}
          <div className="relative">
            <button
              onClick={() => setTypeMenuOpen((o) => !o)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium hover:bg-white/10 transition-colors min-w-[90px] justify-between"
            >
              <span>{currentBlockType?.badge || "Normal"}</span>
              <ChevronDown className="w-3 h-3 text-gray-400" />
            </button>
            {typeMenuOpen && (
              <div className="absolute top-full left-0 mt-1 w-44 bg-white text-gray-800 rounded-lg shadow-xl border py-1 z-50">
                {BLOCK_TYPES.map((t) => (
                  <button
                    key={t.value}
                    onClick={() => changeType(t.value)}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center justify-between ${currentBlockType?.value === t.value ? "text-blue-600 font-semibold" : ""}`}
                  >
                    {t.label}
                    <span className="text-xs text-gray-400 font-mono">{t.badge}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="w-px h-5 bg-gray-600 mx-1" />

          {/* Insert block buttons */}
          {[
            { label: "H2", type: "heading" },
            { label: "H3", type: "subheading" },
            { label: "❝", type: "quote" },
            { label: "•", type: "list" },
          ].map(({ label, type }) => (
            <button
              key={type}
              onClick={() => addBlock(type, focusedIdx !== null ? focusedIdx : form.content.length - 1)}
              title={`Insert ${type}`}
              className="px-2.5 py-1.5 rounded text-sm font-bold hover:bg-white/10 transition-colors text-gray-300 hover:text-white"
            >
              {label}
            </button>
          ))}

          <div className="w-px h-5 bg-gray-600 mx-1" />

          <button
            onClick={() => addBlock("paragraph", focusedIdx !== null ? focusedIdx : form.content.length - 1)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-sm hover:bg-white/10 transition-colors text-gray-300 hover:text-white"
          >
            <Plus className="w-3.5 h-3.5" />
            <span className="text-xs">Paragraph</span>
          </button>
        </div>

        {/* ── Writing area ── */}
        <div className="px-10 sm:px-16 py-10">
          {/* Title */}
          <AutoTextarea
            value={form.title}
            onChange={(e) => handleTitleChange(e.target.value)}
            placeholder="Post title…"
            className="text-4xl font-bold text-gray-900 placeholder:text-gray-300 mb-1 w-full"
          />
          <div className="border-b border-gray-100 mb-8" />

          {/* Content blocks */}
          {form.content.length === 0 ? (
            <p
              className="text-gray-300 text-lg cursor-text"
              onClick={() => addBlock("paragraph")}
            >
              Write here…
            </p>
          ) : (
            <div className="space-y-2">
              {form.content.map((block, idx) => (
                <div key={idx} className="group relative">
                  <AutoTextarea
                    value={block.text || ""}
                    onChange={(e) => updateBlock(idx, { text: e.target.value })}
                    onKeyDown={(e) => handleBlockKeyDown(e, idx)}
                    onFocus={() => setFocusedIdx(idx)}
                    placeholder={
                      block.type === "heading" ? "Heading…"
                      : block.type === "subheading" ? "Sub-heading…"
                      : block.type === "quote" ? "Add a quote…"
                      : block.type === "list" ? "- Item one\n- Item two"
                      : "Write something…"
                    }
                    className={`editor-block-textarea w-full ${BLOCK_STYLE[block.type]}`}
                  />
                  {/* Delete on hover */}
                  <button
                    onClick={() => deleteBlock(idx)}
                    className="absolute -right-7 top-1 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-red-50 text-gray-300 hover:text-red-400"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add paragraph on click at bottom */}
          {form.content.length > 0 && (
            <div
              className="h-16 cursor-text"
              onClick={() => addBlock("paragraph", form.content.length - 1)}
            />
          )}
        </div>
      </div>

      {/* ── Settings Drawer ── */}
      <SettingsDrawer
        form={form}
        set={set}
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />
    </div>
  );
}

// ── Main list view ────────────────────────────────────────────────────────────
export default function BlogManagement() {
  const [view, setView] = useState("list");
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const fetchBlogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      const res = await fetch(`${API_ENDPOINTS.superadminBlogs}?${params}`, {
        headers: authHeaders(), credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to load");
      const json = await res.json();
      setBlogs(Array.isArray(json?.data?.blogs) ? json.data.blogs : []);
      setError("");
    } catch (e) {
      setError(e?.message || "Failed to load blogs");
      setBlogs([]);
    } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { fetchBlogs(); }, [fetchBlogs]);

  const filtered = useMemo(() => {
    const s = q.toLowerCase().trim();
    if (!s) return blogs;
    return blogs.filter((b) => [b.title, b.category, ...(b.tags || [])].join(" ").toLowerCase().includes(s));
  }, [blogs, q]);

  const openEdit = async (blog) => {
    try {
      const res = await fetch(API_ENDPOINTS.superadminBlog(blog._id), { headers: authHeaders(), credentials: "include" });
      const json = await res.json();
      const full = json?.data?.blog;
      if (full) {
        setEditing({
          ...full,
          _slugManual: !!full.slug,
          content: (full.content || []).map((b) =>
            b.type === "list" ? { ...b, text: (b.items || []).map((i) => `- ${i}`).join("\n") } : b
          ),
        });
        setView("editor");
      }
    } catch { toast.error("Failed to load post"); }
  };

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      const isNew = !editing?._id;
      const res = await fetch(
        isNew ? API_ENDPOINTS.superadminBlogs : API_ENDPOINTS.superadminBlog(editing._id),
        { method: isNew ? "POST" : "PATCH", headers: authHeaders(), credentials: "include", body: JSON.stringify(payload) }
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || "Save failed");
      toast.success(isNew ? "Post created!" : "Post updated!");
      setView("list");
      fetchBlogs();
    } catch (e) { toast.error(e?.message || "Save failed"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await fetch(API_ENDPOINTS.superadminBlog(deleteId), { method: "DELETE", headers: authHeaders(), credentials: "include" });
      toast.success("Post deleted");
      setDeleteId(null);
      fetchBlogs();
    } catch { toast.error("Delete failed"); }
  };

  const quickPatch = async (blog, patch) => {
    try {
      const res = await fetch(API_ENDPOINTS.superadminBlog(blog._id), {
        method: "PATCH", headers: authHeaders(), credentials: "include", body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error();
      fetchBlogs();
    } catch { toast.error("Update failed"); }
  };

  if (view === "editor") {
    return (
      <BlogEditor
        initial={editing}
        onSave={handleSave}
        onBack={() => { setView("list"); setEditing(null); }}
        saving={saving}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Blog</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage posts on the public blog page</p>
        </div>
        <Button onClick={() => { setEditing(null); setView("editor"); }} className="gap-1.5">
          <Plus className="w-4 h-4" /> New Post
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search posts…" className="pl-9" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Posts</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="draft">Drafts</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : error ? (
        <div className="text-center py-12 text-red-500">{error}</div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-48 text-gray-400 gap-3">
          <FileText className="w-10 h-10" />
          <p className="text-sm">{q || statusFilter !== "all" ? "No posts match your filters." : "No posts yet."}</p>
        </div>
      ) : (
        <div className="overflow-x-auto border rounded-xl">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="text-left px-4 py-3 font-medium text-gray-600">Title</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Category</th>
                <th className="text-center px-4 py-3 font-medium text-gray-600">Status</th>
                <th className="text-center px-4 py-3 font-medium text-gray-600">Featured</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 whitespace-nowrap">Date</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.map((blog) => (
                <tr key={blog._id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900 line-clamp-1">{blog.title}</div>
                    <div className="text-xs text-gray-400 font-mono mt-0.5">/blog/{blog.slug}</div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className="text-xs">{blog.category}</Badge>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => quickPatch(blog, { isPublished: !blog.isPublished })}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium"
                      style={{ background: blog.isPublished ? "#dcfce7" : "#f3f4f6", color: blog.isPublished ? "#15803d" : "#6b7280" }}
                    >
                      {blog.isPublished ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                      {blog.isPublished ? "Live" : "Draft"}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button onClick={() => quickPatch(blog, { isFeatured: !blog.isFeatured })} className="p-1 rounded hover:bg-yellow-50">
                      {blog.isFeatured ? <Star className="w-4 h-4 text-yellow-500 fill-yellow-400" /> : <StarOff className="w-4 h-4 text-gray-300" />}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                    {formatDate(blog.isPublished ? blog.publishedAt : blog.createdAt)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(blog)} className="h-8 w-8 p-0 text-gray-400 hover:text-blue-600">
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteId(blog._id)} className="h-8 w-8 p-0 text-gray-400 hover:text-red-600">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this post?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone. The post will be removed from the public blog immediately.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
